/**
 * Formulario Público de Registro de Eventos.
 *
 * Este componente proporciona un formulario público y accesible para que los usuarios
 * se registren en eventos. Incluye validación de datos, campos personalizados dinámicos,
 * manejo de estados de carga y éxito, visualización de errores, y diseño responsive
 * utilizando los colores institucionales de IU Digital.
 *
 * @author Eulicer Zapata <eulicer.zapata@iudigital.edu.co>
 * @date [Fecha de creación: 2025-11-12]
 * @updated [Fecha de última modificación: 2025-11-12]
 * @updatedBy Eulicer Zapata <eulicer.zapata@iudigital.edu.co>
 */

import React from "react";
import {
  Container,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Grid,
  Paper,
  Divider,
  MenuItem,
  Avatar,
  Card,
  CardContent,
  FormGroup,
  FormControlLabel,
  Checkbox,
  FormHelperText,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import EventIcon from "@mui/icons-material/Event";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import BannerIUD from "assets/images/BannerIUD.png";

/**
 * Colores institucionales de IU Digital.
 *
 * Define la paleta de colores oficial de la institución para mantener
 * la identidad visual en toda la aplicación.
 *
 * @constant {Object} institutionalColors
 * @property {string} primary - Rojo IUD (#C5292B)
 * @property {string} secondary - Azul IUD (#00416A)
 * @property {string} secondaryDark - Azul IUD oscuro (#002b45)
 * @property {string} accent - Morado IUD (#904DA6)
 * @property {string} dark - Negro IUD (#1D1D1B)
 * @property {string} background - Fondo claro (#f0f2f5)
 * @property {string} white - Blanco (#ffffff)
 * @property {string} success - Verde éxito (#2E7D32)
 */
const institutionalColors = {
  primary: "#C5292B", // Rojo IUD
  secondary: "#00416A", // Azul IUD
  secondaryDark: "#002b45", // Azul IUD más oscuro para borde por defecto
  accent: "#904DA6", // Amarillo IUD
  dark: "#1D1D1B", // Negro IUD
  background: "#f0f2f5",
  white: "#ffffff",
  success: "#2E7D32", // Verde éxito
};

/**
 * Componente de formulario público para registro en eventos.
 *
 * Renderiza diferentes vistas según el estado: carga, error, éxito, o formulario activo.
 * Maneja validación de campos estándar y personalizados, verifica disponibilidad de cupos,
 * y muestra mensajes de confirmación o error según corresponda.
 *
 * @param {Object} props - Propiedades del componente.
 * @param {Object} props.formData - Datos actuales del formulario.
 * @param {Object} props.errors - Errores de validación por campo.
 * @param {boolean} props.loading - Indica si los datos del evento están cargando.
 * @param {boolean} props.submitting - Indica si el formulario se está enviando.
 * @param {Object} props.eventInfo - Información completa del evento.
 * @param {string} props.eventInfo.event_name - Nombre del evento.
 * @param {string} props.eventInfo.card_effective_date - Fecha de inicio del evento.
 * @param {string} props.eventInfo.card_expiration_date - Fecha de fin del evento.
 * @param {Array} props.eventInfo.custom_fields - Campos personalizados del formulario.
 * @param {number} props.eventInfo.max_registrations - Cupos máximos disponibles.
 * @param {number} props.eventInfo.current_registrations - Cupos actuales ocupados.
 * @param {string|null} props.error - Mensaje de error global.
 * @param {boolean} props.success - Indica si el registro fue exitoso.
 * @param {Object|null} props.registeredData - Datos del registro exitoso.
 * @param {Function} props.handleInputChange - Manejador de cambios en campos estándar.
 * @param {Function} props.handleSubmit - Manejador de envío del formulario.
 * @param {Function} props.isFormClosed - Función que verifica si el formulario está cerrado.
 * @param {Function} props.getFormClosureInfo - Función que obtiene información del cierre.
 * @param {Function} props.hasAvailableSlots - Función que verifica disponibilidad de cupos.
 * @param {Function} props.clearError - Función para limpiar mensajes de error.
 * @param {Function} props.handleCustomFieldChange - Manejador de cambios en campos personalizados.
 * @param {Function} props.validateCustomFields - Función de validación de campos personalizados.
 * @param {Function} props.formatEventDateRange - Formatea rango completo de fechas del evento.
 * @param {Function} props.formatEventDateRangeDate - Formatea solo las fechas del evento.
 * @param {Function} props.formatEventDateRangeTime - Formatea solo las horas del evento.
 * @returns {JSX.Element} Componente de formulario público de registro.
 */
export default function PublicRegistration({
  formData,
  errors,
  loading,
  submitting,
  eventInfo,
  error,
  success,
  registeredData,
  handleInputChange,
  handleSubmit,
  isFormClosed,
  getFormClosureInfo,
  hasAvailableSlots,
  clearError,
  handleCustomFieldChange,
  validateCustomFields,
  formatEventDateRange,
  formatEventDateRangeDate,
  formatEventDateRangeTime,
}) {
  const bloodTypes = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"];

  /**
   * Renderiza un campo personalizado individual según su tipo.
   *
   * @param {Object} customField - Definición del campo personalizado.
   * @returns {JSX.Element} Componente del campo renderizado.
   */
  const renderCustomFieldInput = (customField) => {
    const fieldLabel = customField.label || customField.name || customField.id;
    const displayLabel = customField.required
      ? `${customField.label} `
      : `${customField.label} (Opcional)`;

    const fieldStyles = {
      "& .MuiOutlinedInput-root": {
        minHeight: "42px",
        "& fieldset": { borderColor: institutionalColors.secondaryDark },
        "&:hover fieldset": { borderColor: institutionalColors.secondary },
        "&.Mui-focused fieldset": { borderColor: institutionalColors.secondary },
      },
      "& .MuiInputLabel-root.Mui-focused": { color: institutionalColors.secondary },
    };

    if (customField.type === "checklist") {
      return (
        <Grid item xs={12} md={6} key={customField.id}>
          <Box
            sx={{
              position: "relative",
              border: `1px solid ${
                errors[`custom_${customField.id}`] ? "#d32f2f" : institutionalColors.secondaryDark
              }`,
              borderRadius: "4px",
              padding: "8px 14px",
              minHeight: "42px",
              "&:hover": {
                borderColor: errors[`custom_${customField.id}`]
                  ? "#d32f2f"
                  : institutionalColors.secondary,
              },
            }}
          >
            <Typography
              sx={{
                position: "absolute",
                top: "-9px",
                left: "10px",
                backgroundColor: "#fff",
                px: 0.5,
                fontSize: "0.75rem",
                color: errors[`custom_${customField.id}`]
                  ? "#d32f2f"
                  : institutionalColors.secondaryDark,
              }}
            >
              {displayLabel}
            </Typography>
            {customField.options?.map((option) => {
              const currentValues = Array.isArray(formData.custom_fields[fieldLabel])
                ? formData.custom_fields[fieldLabel]
                : [];
              return (
                <FormControlLabel
                  key={option}
                  control={
                    <Checkbox
                      checked={currentValues.includes(option)}
                      onChange={(e) => {
                        const newValues = e.target.checked
                          ? [...currentValues, option]
                          : currentValues.filter((v) => v !== option);
                        handleCustomFieldChange(customField.id, newValues);
                      }}
                      disabled={submitting}
                      size="small"
                      sx={{
                        p: "4px",
                        mr: 1,
                        "&.Mui-checked": { color: institutionalColors.secondary },
                      }}
                    />
                  }
                  label={option}
                  sx={{
                    display: "flex",
                    m: 0,
                    py: 0.3,
                    "& .MuiFormControlLabel-label": {
                      fontSize: "0.8rem !important",
                      fontWeight: "400 !important",
                      color: "#7b809a !important",
                      lineHeight: "1.4 !important",
                    },
                  }}
                />
              );
            })}
          </Box>
          {errors[`custom_${customField.id}`] && (
            <FormHelperText error sx={{ ml: "14px", mt: "3px" }}>
              {errors[`custom_${customField.id}`]}
            </FormHelperText>
          )}
        </Grid>
      );
    }

    if (customField.type === "select") {
      return (
        <Grid item xs={12} md={6} key={customField.id}>
          <TextField
            fullWidth
            select
            required={customField.required}
            label={displayLabel}
            value={formData.custom_fields[fieldLabel] || ""}
            onChange={(e) => handleCustomFieldChange(customField.id, e.target.value)}
            disabled={submitting}
            error={!!errors[`custom_${customField.id}`]}
            helperText={errors[`custom_${customField.id}`]}
            sx={fieldStyles}
          >
            <MenuItem value="">
              <em>Seleccionar...</em>
            </MenuItem>
            {customField.options?.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
      );
    }

    return (
      <Grid item xs={12} md={6} key={customField.id}>
        <TextField
          fullWidth
          required={customField.required}
          label={displayLabel}
          value={formData.custom_fields[fieldLabel] || ""}
          onChange={(e) => handleCustomFieldChange(customField.id, e.target.value)}
          disabled={submitting}
          error={!!errors[`custom_${customField.id}`]}
          helperText={errors[`custom_${customField.id}`]}
          sx={fieldStyles}
        />
      </Grid>
    );
  };

  if (loading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        bgcolor={institutionalColors.background}
      >
        <CircularProgress size={60} sx={{ color: institutionalColors.primary }} />
        <Typography variant="h6" mt={2} color={institutionalColors.secondary}>
          Cargando...
        </Typography>
      </Box>
    );
  }

  if (error && !eventInfo) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        bgcolor={institutionalColors.background}
        p={3}
      >
        <Paper
          elevation={4}
          sx={{
            maxWidth: 600,
            width: "100%",
            overflow: "hidden",
            borderRadius: 3,
            border: `2px solid ${institutionalColors.primary}`,
          }}
        >
          {/* Banner */}
          <Box sx={{ width: "100%" }}>
            <img
              src={BannerIUD}
              alt="IU Digital Banner"
              style={{ width: "100%", height: "auto", objectFit: "contain", display: "block" }}
            />
          </Box>

          {/* Header de error */}
          <Box
            sx={{
              bgcolor: institutionalColors.primary,
              color: "#ffffff",
              py: 2.5,
              px: 3,
              textAlign: "center",
            }}
          >
            <Avatar
              sx={{
                bgcolor: "#ffffff",
                width: 56,
                height: 56,
                margin: "0 auto",
                mb: 1,
              }}
            >
              <Typography
                variant="h4"
                sx={{ color: institutionalColors.primary, fontWeight: "bold" }}
              >
                !
              </Typography>
            </Avatar>
            <Typography variant="h4" fontWeight="bold" sx={{ color: "#ffffff" }}>
              Error
            </Typography>
          </Box>

          {/* Contenido del error */}
          <Box sx={{ p: 4, bgcolor: "#ffffff" }}>
            <Typography variant="body1" color="text.primary" textAlign="center">
              {error}
            </Typography>
          </Box>
        </Paper>
      </Box>
    );
  }

  if (success && registeredData) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        bgcolor={institutionalColors.background}
        p={3}
      >
        <Paper
          elevation={4}
          sx={{
            maxWidth: 700,
            width: "100%",
            overflow: "hidden",
            borderRadius: 3,
            border: `2px solid ${institutionalColors.secondary}`,
          }}
        >
          {/* Banner */}
          <Box sx={{ width: "100%" }}>
            <img
              src={BannerIUD}
              alt="IU Digital Banner"
              style={{ width: "100%", height: "auto", objectFit: "contain", display: "block" }}
            />
          </Box>

          {/* Header de éxito */}
          <Box
            sx={{
              bgcolor: institutionalColors.accent,
              color: "#ffffff",
              py: 2.5,
              px: 3,
              textAlign: "center",
            }}
          >
            <Avatar
              sx={{
                bgcolor: "#ffffff",
                width: 56,
                height: 56,
                margin: "0 auto",
                mb: 1,
              }}
            >
              <CheckCircleIcon sx={{ fontSize: 36, color: institutionalColors.accent }} />
            </Avatar>
            <Typography variant="h4" fontWeight="bold" sx={{ color: "#ffffff" }}>
              ¡Registro Exitoso!
            </Typography>
          </Box>

          {/* Contenido */}
          <Box sx={{ p: 3 }}>
            {/* Información del Evento - Tarjeta */}
            <Paper
              elevation={2}
              sx={{
                mb: 2.5,
                p: 2.5,
                borderRadius: 2,
                border: `1px solid ${institutionalColors.secondary}30`,
              }}
            >
              <Box display="flex" alignItems="center" justifyContent="center" mb={2}>
                <EventIcon sx={{ fontSize: 28, mr: 1, color: institutionalColors.secondary }} />
                <Typography
                  variant="h6"
                  sx={{
                    color: institutionalColors.secondary,
                    fontWeight: "700",
                    letterSpacing: "0.3px",
                  }}
                >
                  Información del Evento
                </Typography>
              </Box>
              <Divider sx={{ mb: 2.5, borderColor: `${institutionalColors.secondary}30` }} />
              <Grid container spacing={2.5}>
                <Grid item xs={12}>
                  <Box textAlign={{ xs: "center", sm: "left" }}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: institutionalColors.dark,
                        fontWeight: "600",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        fontSize: "0.7rem",
                      }}
                      display="block"
                      mb={0.8}
                    >
                      Evento
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: institutionalColors.secondary,
                        fontWeight: "600",
                        fontSize: "1.05rem",
                      }}
                    >
                      {registeredData.event?.name || eventInfo.event_name}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box textAlign={{ xs: "center", sm: "left" }}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: institutionalColors.dark,
                        fontWeight: "600",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        fontSize: "0.7rem",
                      }}
                      display="block"
                      mb={0.8}
                    >
                      Fecha
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: institutionalColors.secondary,
                        fontWeight: "600",
                        fontSize: "0.95rem",
                      }}
                    >
                      {formatEventDateRangeDate(
                        eventInfo.card_effective_date,
                        eventInfo.card_expiration_date
                      )}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box textAlign={{ xs: "center", sm: "left" }}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: institutionalColors.dark,
                        fontWeight: "600",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        fontSize: "0.7rem",
                      }}
                      display="block"
                      mb={0.8}
                    >
                      Hora
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: institutionalColors.secondary,
                        fontWeight: "600",
                        fontSize: "0.95rem",
                      }}
                    >
                      {formatEventDateRangeTime(
                        eventInfo.card_effective_date,
                        eventInfo.card_expiration_date
                      )}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Paper>

            {/* Tus Datos - Tarjeta */}
            <Paper
              elevation={2}
              sx={{
                mb: 2.5,
                p: 2.5,
                borderRadius: 2,
                border: `1px solid ${institutionalColors.secondary}30`,
              }}
            >
              <Box display="flex" alignItems="center" justifyContent="center" mb={2}>
                <PersonAddIcon sx={{ fontSize: 28, mr: 1, color: institutionalColors.secondary }} />
                <Typography
                  variant="h6"
                  sx={{
                    color: institutionalColors.secondary,
                    fontWeight: "700",
                    letterSpacing: "0.3px",
                  }}
                >
                  Tus Datos
                </Typography>
              </Box>
              <Divider sx={{ mb: 2.5, borderColor: `${institutionalColors.secondary}30` }} />
              <Grid container spacing={2.5}>
                <Grid item xs={12} sm={6}>
                  <Box textAlign={{ xs: "center", sm: "left" }}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: institutionalColors.dark,
                        fontWeight: "600",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        fontSize: "0.7rem",
                      }}
                      display="block"
                      mb={0.8}
                    >
                      Nombre completo
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: institutionalColors.secondary,
                        fontWeight: "600",
                        fontSize: "0.95rem",
                      }}
                    >
                      {registeredData.card_user?.name} {registeredData.card_user?.lastname}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box textAlign={{ xs: "center", sm: "left" }}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: institutionalColors.dark,
                        fontWeight: "600",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        fontSize: "0.7rem",
                      }}
                      display="block"
                      mb={0.8}
                    >
                      Documento de identificación
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: institutionalColors.secondary,
                        fontWeight: "600",
                        fontSize: "0.95rem",
                      }}
                    >
                      {registeredData.card_user?.number_id}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={registeredData.card_user?.blood_type ? 6 : 12}>
                  <Box textAlign={{ xs: "center", sm: "left" }}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: institutionalColors.dark,
                        fontWeight: "600",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        fontSize: "0.7rem",
                      }}
                      display="block"
                      mb={0.8}
                    >
                      Correo electrónico
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: institutionalColors.secondary,
                        fontWeight: "600",
                        fontSize: "0.95rem",
                      }}
                    >
                      {registeredData.card?.email}
                    </Typography>
                  </Box>
                </Grid>
                {registeredData.card_user?.blood_type && (
                  <Grid item xs={12} sm={6}>
                    <Box textAlign={{ xs: "center", sm: "left" }}>
                      <Typography
                        variant="caption"
                        sx={{
                          color: institutionalColors.dark,
                          fontWeight: "600",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                          fontSize: "0.7rem",
                        }}
                        display="block"
                        mb={0.8}
                      >
                        Tipo de sangre
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: institutionalColors.secondary,
                          fontWeight: "600",
                          fontSize: "0.95rem",
                        }}
                      >
                        {registeredData.card_user?.blood_type}
                      </Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Paper>

            {/* Datos Adicionales - Tarjeta (solo si existen custom_data) */}
            {registeredData.card?.custom_data &&
              Object.keys(registeredData.card.custom_data).length > 0 && (
                <Paper
                  elevation={2}
                  sx={{
                    mb: 2.5,
                    p: 2.5,
                    borderRadius: 2,
                    border: `1px solid ${institutionalColors.secondary}30`,
                  }}
                >
                  <Box display="flex" alignItems="center" justifyContent="center" mb={2}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 28,
                        height: 28,
                        mr: 1,
                      }}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill={institutionalColors.secondary}
                        width="28"
                        height="28"
                      >
                        <path d="M19 3h-4.18C14.4 1.84 13.3 1 12 1c-1.3 0-2.4.84-2.82 2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 0c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm2 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z" />
                      </svg>
                    </Box>
                    <Typography
                      variant="h6"
                      sx={{
                        color: institutionalColors.secondary,
                        fontWeight: "700",
                        letterSpacing: "0.3px",
                      }}
                    >
                      Datos Adicionales
                    </Typography>
                  </Box>
                  <Divider sx={{ mb: 2.5, borderColor: `${institutionalColors.secondary}30` }} />
                  <Grid container spacing={2.5}>
                    {Object.entries(registeredData.card.custom_data).map(([key, fieldData]) => (
                      <Grid item xs={12} sm={6} key={key}>
                        <Box textAlign={{ xs: "center", sm: "left" }}>
                          <Typography
                            variant="caption"
                            sx={{
                              color: institutionalColors.dark,
                              fontWeight: "600",
                              textTransform: "uppercase",
                              letterSpacing: "0.5px",
                              fontSize: "0.7rem",
                            }}
                            display="block"
                            mb={0.8}
                          >
                            {fieldData.label}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: institutionalColors.secondary,
                              fontWeight: "600",
                              fontSize: "0.95rem",
                            }}
                          >
                            {fieldData.value}
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Paper>
              )}

            {/* Alerta informativa */}
            <Alert
              severity="success"
              sx={{
                mb: 2.5,
                bgcolor: `${institutionalColors.success}15`,
                border: `1px solid ${institutionalColors.success}40`,
                borderRadius: 2,
                "& .MuiAlert-icon": {
                  color: institutionalColors.success,
                },
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: "500",
                  color: institutionalColors.dark,
                  lineHeight: 1.6,
                }}
              >
                Tu registro ha sido procesado correctamente. Pronto recibirás la invitación oficial
                en tu correo electrónico.
              </Typography>
            </Alert>
          </Box>
        </Paper>
      </Box>
    );
  }

  // Verificar si el formulario está disponible
  const formClosed = isFormClosed();
  const formClosureInfo = getFormClosureInfo();
  const noSlots = !hasAvailableSlots();

  return (
    <Box minHeight="100vh" bgcolor={institutionalColors.background} py={4}>
      <Container maxWidth="md">
        {/* Header del evento */}
        <Paper
          elevation={4}
          sx={{
            mb: 4,
            overflow: "hidden",
            borderRadius: 3,
            border: `2px solid ${institutionalColors.secondary}`,
          }}
        >
          {/* Banner aligned to card width (responsive, no crop) */}
          {BannerIUD && (
            <Box sx={{ width: "100%" }}>
              <img
                src={BannerIUD}
                alt="IU Digital Banner"
                style={{ width: "100%", height: "auto", objectFit: "contain", display: "block" }}
              />
            </Box>
          )}
          <Box
            sx={{
              bgcolor: institutionalColors.secondary,
              color: "white",
              p: 4,
            }}
          >
            <Box display="flex" alignItems="center" mb={2}>
              <EventIcon sx={{ fontSize: 50, mr: 2, color: "#ffffff" }} />
              <Typography variant="h3" fontWeight="bold" sx={{ color: "#ffffff" }}>
                Evento: {eventInfo.event_name}
              </Typography>
            </Box>
            {eventInfo.event_description && (
              <Typography variant="h6" sx={{ color: "#ffffff", opacity: 0.95 }}>
                {eventInfo.event_description}
              </Typography>
            )}
          </Box>

          {/* Información de vigencia del carnet - Fondo Blanco */}
          <Box p={3} bgcolor={institutionalColors.white}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Box textAlign={{ xs: "center", sm: "left" }}>
                  <Typography
                    variant="body2"
                    color={institutionalColors.secondary}
                    fontWeight="600"
                    display="block"
                    mb={0.5}
                  >
                    Fecha
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: "600" }}>
                    {formatEventDateRangeDate(
                      eventInfo.card_effective_date,
                      eventInfo.card_expiration_date
                    )}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box textAlign={{ xs: "center", sm: "left" }}>
                  <Typography
                    variant="body2"
                    color={institutionalColors.secondary}
                    fontWeight="600"
                    display="block"
                    mb={0.5}
                  >
                    Hora
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: "600" }}>
                    {formatEventDateRangeTime(
                      eventInfo.card_effective_date,
                      eventInfo.card_expiration_date
                    )}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* Formulario de registro integrado */}
          {!formClosed && !noSlots && (
            <Box sx={{ p: 4, bgcolor: institutionalColors.white, borderTop: `1px solid #e0e0e0` }}>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                mb={4}
                pb={2}
                borderBottom={`2px solid ${institutionalColors.primary}`}
              >
                <PersonAddIcon sx={{ fontSize: 35, mr: 1.5, color: institutionalColors.primary }} />
                <Typography variant="h4" fontWeight="bold" color={institutionalColors.secondary}>
                  Formulario de Registro
                </Typography>
              </Box>

              {error && (
                <Alert
                  severity="error"
                  onClose={clearError}
                  variant="outlined"
                  sx={{
                    mb: 3,
                    bgcolor: "#fff",
                    borderColor: institutionalColors.primary,
                    borderWidth: "2px",
                    borderRadius: 2,
                    color: institutionalColors.primary,
                    fontWeight: "bold",
                    "& .MuiAlert-icon": {
                      color: institutionalColors.primary,
                    },
                    "& .MuiAlert-message": {
                      width: "100%",
                      fontWeight: "bold",
                    },
                  }}
                >
                  {error}
                </Alert>
              )}

              <form onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      required
                      label="Nombre"
                      value={formData.name}
                      onChange={(e) => handleInputChange("name", e.target.value)}
                      error={!!errors.name}
                      helperText={errors.name}
                      disabled={submitting}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": {
                            borderColor: institutionalColors.secondaryDark,
                          },
                          "&:hover fieldset": {
                            borderColor: institutionalColors.secondary,
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: institutionalColors.secondary,
                          },
                        },
                        "& .MuiInputLabel-root.Mui-focused": {
                          color: institutionalColors.secondary,
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      required
                      label="Apellido"
                      value={formData.lastname}
                      onChange={(e) => handleInputChange("lastname", e.target.value)}
                      error={!!errors.lastname}
                      helperText={errors.lastname}
                      disabled={submitting}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": {
                            borderColor: institutionalColors.secondaryDark,
                          },
                          "&:hover fieldset": {
                            borderColor: institutionalColors.secondary,
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: institutionalColors.secondary,
                          },
                        },
                        "& .MuiInputLabel-root.Mui-focused": {
                          color: institutionalColors.secondary,
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      required
                      label="Documento de identificación"
                      value={formData.number_id}
                      onChange={(e) => handleInputChange("number_id", e.target.value)}
                      error={!!errors.number_id}
                      helperText={errors.number_id}
                      disabled={submitting}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": {
                            borderColor: institutionalColors.secondaryDark,
                          },
                          "&:hover fieldset": {
                            borderColor: institutionalColors.secondary,
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: institutionalColors.secondary,
                          },
                        },
                        "& .MuiInputLabel-root.Mui-focused": {
                          color: institutionalColors.secondary,
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      required
                      type="email"
                      label="Correo Electrónico"
                      value={formData.email}
                      onChange={(e) => handleInputChange("email", e.target.value)}
                      error={!!errors.email}
                      helperText={errors.email}
                      disabled={submitting}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": {
                            borderColor: institutionalColors.secondaryDark,
                          },
                          "&:hover fieldset": {
                            borderColor: institutionalColors.secondary,
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: institutionalColors.secondary,
                          },
                        },
                        "& .MuiInputLabel-root.Mui-focused": {
                          color: institutionalColors.secondary,
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      select
                      label="Tipo de Sangre (Opcional)"
                      value={formData.blood_type}
                      onChange={(e) => handleInputChange("blood_type", e.target.value)}
                      disabled={submitting}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          minHeight: "42px",
                          "& fieldset": {
                            borderColor: institutionalColors.secondaryDark,
                          },
                          "&:hover fieldset": {
                            borderColor: institutionalColors.secondary,
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: institutionalColors.secondary,
                          },
                        },
                        "& .MuiInputLabel-root.Mui-focused": {
                          color: institutionalColors.secondary,
                        },
                      }}
                    >
                      <MenuItem value="">
                        <em>Seleccionar...</em>
                      </MenuItem>
                      {bloodTypes.map((type) => (
                        <MenuItem key={type} value={type}>
                          {type}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  {/* Campos Personalizados Dinámicos (estructura mixta) */}
                  {eventInfo?.custom_fields &&
                    eventInfo.custom_fields.length > 0 &&
                    eventInfo.custom_fields.map((item) => {
                      if (item.fields && Array.isArray(item.fields)) {
                        return (
                          <Grid item xs={12} key={item.id}>
                            {item.name && (
                              <Typography
                                variant="subtitle1"
                                sx={{
                                  fontWeight: 600,
                                  color: institutionalColors.secondary,
                                  mb: 2,
                                  mt: 2,
                                  borderBottom: `2px solid ${institutionalColors.secondary}`,
                                  pb: 0.5,
                                }}
                              >
                                {item.name}
                              </Typography>
                            )}
                            <Grid container spacing={3}>
                              {item.fields.map((field) => renderCustomFieldInput(field))}
                            </Grid>
                          </Grid>
                        );
                      }
                      if (item.id && item.type) {
                        return renderCustomFieldInput(item);
                      }
                      return null;
                    })}
                </Grid>

                <Box mt={5} display="flex" justifyContent="center">
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={submitting}
                    disableRipple
                    disableElevation
                    startIcon={
                      submitting ? (
                        <CircularProgress size={20} sx={{ color: "#ffffff" }} />
                      ) : (
                        <PersonAddIcon />
                      )
                    }
                    sx={{
                      minWidth: 250,
                      py: 1.5,
                      fontSize: "1.1rem",
                      fontWeight: "bold",
                      color: "#ffffff",
                      bgcolor: institutionalColors.primary,
                      boxShadow: `0 4px 15px ${institutionalColors.primary}40`,
                      transition: "background-color 0.15s ease-in-out",
                      "&:hover": {
                        bgcolor: institutionalColors.primary,
                        boxShadow: `0 4px 15px ${institutionalColors.primary}40`,
                        transform: "none",
                      },
                      "&:active": {
                        bgcolor: `${institutionalColors.secondaryDark} !important`,
                        boxShadow: `0 4px 15px ${institutionalColors.secondaryDark}40 !important`,
                        transform: "none",
                      },
                      "&:focus:not(:active)": {
                        bgcolor: institutionalColors.primary,
                        boxShadow: `0 4px 15px ${institutionalColors.primary}40`,
                      },
                      "&:disabled": {
                        bgcolor: institutionalColors.primary,
                        color: "#ffffff",
                        opacity: 0.7,
                        boxShadow: `0 4px 15px ${institutionalColors.primary}40`,
                      },
                      "&.Mui-disabled": {
                        bgcolor: institutionalColors.primary,
                        color: "#ffffff",
                        opacity: 0.7,
                      },
                      "&.Mui-focusVisible:not(:active)": {
                        bgcolor: institutionalColors.primary,
                      },
                    }}
                  >
                    {submitting ? "Registrando..." : "Registrarse"}
                  </Button>
                </Box>
              </form>
            </Box>
          )}
        </Paper>

        {/* Alertas de estado del formulario */}
        {formClosed && (
          <Paper
            elevation={3}
            sx={{
              mb: 3,
              overflow: "hidden",
              borderRadius: 2,
              border: `2px solid ${institutionalColors.primary}`,
            }}
          >
            <Box
              sx={{
                bgcolor: institutionalColors.primary,
                color: "#ffffff",
                py: 2,
                px: 3,
                display: "flex",
                alignItems: "center",
              }}
            >
              <Box
                sx={{
                  bgcolor: "#ffffff",
                  borderRadius: "50%",
                  width: 40,
                  height: 40,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mr: 2,
                }}
              >
                <Typography
                  variant="h5"
                  sx={{ color: institutionalColors.primary, fontWeight: "bold" }}
                >
                  ⚠
                </Typography>
              </Box>
              <Typography variant="h5" fontWeight="bold" sx={{ color: "#ffffff" }}>
                {formClosureInfo.title || "Formulario No Disponible"}
              </Typography>
            </Box>
            <Box sx={{ p: 3, bgcolor: "#ffffff" }}>
              <Typography variant="body1" color="text.primary">
                {formClosureInfo.message ||
                  "Este formulario de registro no está disponible en este momento."}
              </Typography>
            </Box>
          </Paper>
        )}

        {noSlots && !formClosed && (
          <Paper
            elevation={3}
            sx={{
              mb: 3,
              overflow: "hidden",
              borderRadius: 2,
              border: `2px solid ${institutionalColors.primary}`,
            }}
          >
            <Box
              sx={{
                bgcolor: institutionalColors.primary,
                color: "#ffffff",
                py: 2,
                px: 3,
                display: "flex",
                alignItems: "center",
              }}
            >
              <Box
                sx={{
                  bgcolor: "#ffffff",
                  borderRadius: "50%",
                  width: 40,
                  height: 40,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mr: 2,
                }}
              >
                <Typography
                  variant="h5"
                  sx={{ color: institutionalColors.primary, fontWeight: "bold" }}
                >
                  ✕
                </Typography>
              </Box>
              <Typography variant="h5" fontWeight="bold" sx={{ color: "#ffffff" }}>
                Cupo Máximo Alcanzado
              </Typography>
            </Box>
            <Box sx={{ p: 3, bgcolor: "#ffffff" }}>
              <Typography variant="body1" color="text.primary">
                Este evento ha alcanzado el límite máximo de registros permitidos. Te invitamos a
                estar atento a futuros eventos.
              </Typography>
            </Box>
          </Paper>
        )}

        {/* Footer */}
        <Box mt={4} textAlign="center">
          <Divider sx={{ mb: 2, borderColor: institutionalColors.secondary }} />
          <Typography
            variant="body2"
            fontWeight="600"
            sx={{ color: institutionalColors.dark, mb: 0.5 }}
          >
            © {new Date().getFullYear()} IU Digital - Sistema de Carnetización
          </Typography>
          <Typography variant="caption" sx={{ color: institutionalColors.dark }}>
            Institución Universitaria Digital de Antioquia
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}
